import { Alert, Badge, Button, Card, Col, ListGroup, Row, Spinner } from "react-bootstrap";
import type {
    IMappingValidationResult,
    ISpreadsheetTemplateActive,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { TemplateValidationSummary, formatValidationIssueContext } from "./TemplateValidationSummary";
import {
    getDisplayedMappingValidationWarningCount,
    isExportReadyForDisplay,
} from "./mappingValidationDisplay";
import { TEST_EXPORT_BUTTON_LABEL, canGenerateTestExport } from "./wasteReturnTemplateTestExport";
import type { WasteReturnImportStep } from "./wasteReturnImportSteps";
import { WASTE_RETURN_IMPORT_STEP_LABELS } from "./wasteReturnImportSteps";

export interface WasteReturnValidateSaveStepProps {
    activeTemplate: ISpreadsheetTemplateActive | null;
    validation: IMappingValidationResult | null;
    finishing: boolean;
    testExporting: boolean;
    finishAttempted: boolean;
    onFinish: () => void;
    onTestExport: () => void;
    onGoToStep: (step: WasteReturnImportStep) => void;
}

function getSuggestedFixStep(issue: { section?: string | null; fieldKey?: string | null; table?: string | null }): WasteReturnImportStep {
    const haystack = `${issue.section ?? ""} ${issue.fieldKey ?? ""} ${issue.table ?? ""}`.toLowerCase();
    if (haystack.includes("listsource") || haystack.includes("list source") || haystack.includes("extracted")) {
        return "sync";
    }
    return "map";
}

export function WasteReturnValidateSaveStep({
    activeTemplate,
    validation,
    finishing,
    testExporting,
    finishAttempted,
    onFinish,
    onTestExport,
    onGoToStep,
}: WasteReturnValidateSaveStepProps) {
    const testExportEnabled = canGenerateTestExport(activeTemplate, validation, testExporting || finishing);
    const showSuccess = finishAttempted && validation?.isValid;
    const showFailure = finishAttempted && validation && !validation.isValid;
    const displayedWarningCount = validation ? getDisplayedMappingValidationWarningCount(validation) : 0;
    const exportReadyForDisplay = validation ? isExportReadyForDisplay(validation) : false;

    return (
        <Card className="mb-4">
            <Card.Header>Validate & save</Card.Header>
            <Card.Body>
                <p className="text-muted small">
                    Run a full validation check, save the mapping as your export configuration, and optionally download a test workbook.
                </p>

                {showFailure && (
                    <Alert variant="danger">
                        <strong>Validation failed — export is not ready.</strong>
                        <p className="mb-2 mt-2">
                            Fix the issues below, then return here to finish. List-source problems are usually resolved on the{" "}
                            <strong>{WASTE_RETURN_IMPORT_STEP_LABELS.sync}</strong> step; field address problems on{" "}
                            <strong>{WASTE_RETURN_IMPORT_STEP_LABELS.map}</strong>.
                        </p>
                        <ListGroup variant="flush" className="mb-3">
                            {validation.errors.map((issue, index) => {
                                const fixStep = getSuggestedFixStep(issue);
                                return (
                                    <ListGroup.Item key={`${issue.code}-${index}`} className="px-0 bg-transparent text-danger">
                                        <div>
                                            <Badge bg="danger" className="me-2">
                                                {issue.code}
                                            </Badge>
                                            {issue.message}
                                        </div>
                                        <div className="text-muted small">{formatValidationIssueContext(issue)}</div>
                                        <Button
                                            variant="link"
                                            className="p-0 small"
                                            onClick={() => onGoToStep(fixStep)}
                                        >
                                            Go to {WASTE_RETURN_IMPORT_STEP_LABELS[fixStep]} to fix
                                        </Button>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                        {displayedWarningCount > 0 && (
                            <div className="small text-muted">
                                {displayedWarningCount} warning(s) also need review once errors are fixed.
                            </div>
                        )}
                    </Alert>
                )}

                {showSuccess && activeTemplate && (
                    <Alert variant="success">
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                            <strong>Import complete</strong>
                            <Badge bg={exportReadyForDisplay ? "success" : "warning"}>
                                {exportReadyForDisplay ? "Export ready" : "Saved with warnings"}
                            </Badge>
                        </div>
                        <div className="small">
                            <div>
                                <span className="text-muted">Template: </span>
                                {activeTemplate.originalFileName}
                            </div>
                            <div>
                                <span className="text-muted">Validated: </span>
                                {new Date(validation.checkedAtUtc).toLocaleString()}
                            </div>
                        </div>
                    </Alert>
                )}

                <Row className="g-2 mb-4">
                    <Col xs="auto">
                        <Button variant="primary" onClick={onFinish} disabled={finishing || testExporting}>
                            {finishing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Validating & saving…
                                </>
                            ) : (
                                "Save and complete"
                            )}
                        </Button>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="outline-success"
                            onClick={onTestExport}
                            disabled={!testExportEnabled}
                            title="Generate a sample workbook using the saved mapping. This is not a production waste return export."
                        >
                            {testExporting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Generating…
                                </>
                            ) : (
                                TEST_EXPORT_BUTTON_LABEL
                            )}
                        </Button>
                    </Col>
                </Row>

                <TemplateValidationSummary validation={validation} />
            </Card.Body>
        </Card>
    );
}
